import {
  LightObject,
  LightModel,
  BookingDB,
  LookupRequest
} from "./BookingLightDB"
import { computed, observable } from "mobx"
import { uuid, hasMinChars } from "../../light/utils"
import dayjs, { Dayjs } from "dayjs"
import { $Session, Session } from "./Session"

export interface $SessionType extends LightObject {
  name: string
  capacity: number
  firstPassCatalogItemId: string
  secondPassCatalogItemId: string

  firstPassPriceInCents: number
  secondPassPriceInCents: number
  durationInMinutes: number
}

export class SessionType implements LightModel {
  constructor(db: BookingDB, object: $SessionType) {
    this.db = db
    this.object = object
  }

  @computed
  get id() {
    return this.object.$id
  }

  @computed
  get type() {
    return this.object.$type
  }

  children = null

  @computed
  get isValid() {
    return hasMinChars(this.object.name, 1) && this.object.capacity > 0
  }

  @computed
  get invalidMessage() {
    return undefined
  }

  clean() {}

  @computed
  get lookupValues() {
    return [this.object.name]
  }

  db: BookingDB

  @observable
  object: $SessionType

  numOfSlots = 23

  startTime(date: Dayjs): Dayjs {
    let newDate = date
      .set("hour", 9)
      .set("minute", 0)
      .set("second", 0)
      .set("millisecond", 0)
    return newDate
  }

  timeSlotsForDay(date: Dayjs) {
    const timeSlots: Dayjs[] = []
    let time = this.startTime(date)
    for (let i = 0; i < this.numOfSlots; i++) {
      timeSlots.push(time)
      time = time.add(30, "minute")
    }
    return timeSlots
  }

  @observable
  sessionCache: Map<string, Session[]> = observable.map()

  sessionsForDay(date = dayjs()): Session[] {
    const dateString = date.toISOString()
    if (this.sessionCache.has(dateString))
      return this.sessionCache.get(dateString) as Session[]
    let sessions: Session[] = []
    this.timeSlotsForDay(date).forEach(t => {
      const timeSlotIndex = this.timeSlotIndex(t)
      const $session = this.db.lookupLocal({
        lookupKey: timeSlotIndex,
        type: "session"
      }) as $Session | null
      if ($session) sessions.push(new Session(this.db, $session))
    })

    if (this.db.isClient) this.sessionCache.set(dateString, sessions)
    return sessions
  }

  nextAvailableSession(date: Dayjs): Session | undefined {
    const sessions = this.sessionsForDay(date)
    return sessions.find(s => s.capacityLeft > 0)
  }

  lookupsForDay(date: Dayjs): LookupRequest[] {
    return this.timeSlotsForDay(date).map(t => this.lookupForSessionWithDate(t))
  }

  lookupForSessionWithDate(date: Dayjs): LookupRequest {
    const timeSlotIndex = this.timeSlotIndex(date)

    return {
      type: "session",
      lookupKey: timeSlotIndex,
      createIfNotFound: this.$sessionWithDate(date.toISOString())
    }
  }

  timeSlotIndex(date = dayjs()) {
    return `${this.object.name}:${date.format("MMM:DD:YYYY:h:mma")}`
  }

  $sessionWithDate(dateString: string): $Session {
    return {
      $id: uuid(),
      $type: "session",
      $version: uuid(),
      $parentVersion: null,
      $versionDateString: new Date().toISOString(),
      dateString: dateString,
      $sessionGroups: {},
      $sessionType: this.object.$id
    }
  }
}
